/* eslint-disable css-modules/no-undef-class */
/* eslint-disable react-hooks/exhaustive-deps */
import * as maptilersdk from '@maptiler/sdk';
import { Marker } from '@maptiler/sdk';
import classNames from 'classnames';
import { Feature, FeatureCollection, GeoJSON } from 'geojson';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Icon, { IconSize } from '@/components/atoms/icon/icon';
import mapConfig from '@/components/molecules/map/map.config';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import styles from './map.module.scss';

export interface MapProps {
  /** The active item */
  activeItem?: Feature | any;
  /** Data to apply additional styling */
  additionalStyles?: {
    classes: string[];
    icon: string;
    id: string;
  }[];
  /** The content to show on top of the map on fullscreen */
  children?: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /** Whether the markers should be connected */
  connectMarkers?: boolean;
  /** The position of the control buttons */
  controlPosition?: maptilersdk.ControlPosition;
  /** The zoom level of the map when interacting with items */
  flyToZoom?: number;
  /** The items to show on the map */
  items: {
    landmarks?: FeatureCollection | any;
    markers?: FeatureCollection | any;
    pois?: FeatureCollection | any;
  };
  /** An event that is triggered when clicking a landmark */
  onClickLandmark?: (feature: Feature | any) => void;
  /** An event that is triggered when clicking a marker */
  onClickMarker?: (feature: Feature | any) => void;
  /** The transport mode icons to show on the lines between the markers */
  transportModes?: FeatureCollection | any;
}

const FULLSCREEN_CHANGE_EVENTS = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange',
];

export type MapHandle = {
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  onFullscreenChange: (fn: (value: boolean) => void) => void;
};

maptilersdk.config.apiKey = `${process.env.NEXT_PUBLIC_MAP_API_KEY}`;
maptilersdk.config.primaryLanguage = maptilersdk.Language.DANISH;

const FIT_BOUNDS_MAX_ZOOM = 16;
const FIT_BOUNDS_PADDING = 128;
const MAP_ANIMATION_DURATION = 1000;

let map: maptilersdk.Map | any;

interface MarkerRef {
  item: Marker;
}

type MarkersCollection = Record<string, Marker>;

const Map = forwardRef<MapHandle, MapProps>(
  (
    {
      activeItem,
      additionalStyles = [],
      children,
      className,
      connectMarkers = false,
      controlPosition,
      flyToZoom = 16,
      items,
      onClickLandmark,
      onClickMarker,
      transportModes,
    },
    ref,
  ) => {
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const landmarksRef = useRef<Record<string, MarkerRef>>({});
    const mapRef = useRef<HTMLDivElement>(null);
    const markersRef = useRef<Record<string, MarkerRef>>({});
    const poisRef = useRef<Record<string, MarkerRef>>({});
    const onFullscreenChangeRef = useRef<(value: boolean) => void>(() => {});

    const hasTransportModes =
      transportModes &&
      transportModes.features &&
      transportModes.features.length > 0;

    const bounds = useMemo(() => {
      const coordinates: [number, number][] = [];

      Object.values(items).forEach(collection => {
        if (collection && collection.features?.length > 0) {
          collection.features.forEach((feature: any) => {
            if (feature.geometry?.coordinates) {
              coordinates.push(feature.geometry.coordinates);
            }
          });
        }
      });

      if (coordinates.length > 0) {
        const [[lng, lat]] = coordinates;
        const northEast: maptilersdk.LngLatLike = { lat, lng };
        const southWest: maptilersdk.LngLatLike = { lat, lng };

        coordinates.forEach(([longitude, latitude]) => {
          northEast.lat = Math.max(northEast.lat, latitude);
          northEast.lng = Math.max(northEast.lng, longitude);
          southWest.lat = Math.min(southWest.lat, latitude);
          southWest.lng = Math.min(southWest.lng, longitude);
        });

        return [southWest, northEast];
      }
    }, [items.markers, items.pois, items.landmarks]);

    const renderIcon = useCallback((icon: IconName, size: IconSize) => {
      return renderToStaticMarkup(
        <Icon className="icon" name={icon} size={size} />,
      );
    }, []);

    const removeActiveStyles = () => {
      Object.values(markersRef.current).forEach(ref => {
        ref.item.getElement().classList.remove('isActive');
      });
    };

    const getIsFullScreen = () =>
      typeof document !== 'undefined' &&
      !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).webkitCurrentFullScreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );

    const enterFullscreen = () => {
      if (!mapRef.current) return;

      const mapContainer = mapRef.current;

      if (mapContainer.requestFullscreen)
        return mapContainer.requestFullscreen();

      if ((mapContainer as any).webkitRequestFullscreen)
        return (mapContainer as any).webkitRequestFullscreen();

      if ((mapContainer as any).mozRequestFullScreen)
        return (mapContainer as any).mozRequestFullScreen();

      if ((mapContainer as any).msRequestFullscreen)
        return (mapContainer as any).msRequestFullscreen();
    };

    const exitFullscreen = () => {
      if (!getIsFullScreen()) return;

      const exitFs =
        document.exitFullscreen ||
        (document as any).webkitExitFullscreen ||
        (document as any).mozCancelFullScreen ||
        (document as any).msExitFullscreen;

      exitFs?.call(document);
    };

    useImperativeHandle(ref, () => ({
      enterFullscreen,
      exitFullscreen,
      onFullscreenChange: fn => {
        onFullscreenChangeRef.current = fn;
      },
    }));

    const removeSource = sourceId => {
      if (!map.getSource(sourceId)) return;

      const layers = map.getStyle().layers;

      layers.forEach(layer => {
        if (layer.source === sourceId) {
          map.removeLayer(layer.id);
        }
      });

      map.removeSource(sourceId);
    };

    const getVisibleUniqueFeatures = async sourceId => {
      const visibleFeatures = map.querySourceFeatures(sourceId);

      const uniqueClusterIds = new Set();
      const uniqueMarkerIds = new Set();

      const standaloneMarkers: Feature[] = [];
      const clusters: Feature[] = [];

      for (const feature of visibleFeatures) {
        if (feature.properties.cluster) {
          if (!uniqueClusterIds.has(feature.properties.cluster_id)) {
            clusters.push(feature);
            uniqueClusterIds.add(feature.properties.cluster_id);
          }
        } else {
          if (!uniqueMarkerIds.has(feature.properties.id)) {
            standaloneMarkers.push(feature);
            uniqueMarkerIds.add(feature.properties.id);
          }
        }
      }

      for (const cluster of clusters) {
        const clusterId = cluster?.properties?.cluster_id;

        const leaves = await map
          .getSource(sourceId)
          .getClusterLeaves(clusterId, Infinity, 0);

        for (const leaf of leaves) {
          uniqueMarkerIds.delete(leaf.properties.id);
        }
      }

      return {
        clusters,
        markers: standaloneMarkers,
      };
    };

    const animateOpacity = (
      layer: string,
      clusterId: any,
      startOpacity: number,
      endOpacity: number,
    ) => {
      const duration = 200;
      const frameRate = 60;
      const frameCount = Math.round((duration / 1000) * frameRate);
      let currentFrame = 0;

      const animate = () => {
        if (currentFrame >= frameCount) return;

        const opacityStep = (endOpacity - startOpacity) / frameCount;
        const currentOpacity = startOpacity + opacityStep * currentFrame;

        const opacityExpression = [
          'case',
          ['==', ['get', 'cluster_id'], clusterId],
          currentOpacity,
          0,
        ];

        map.setPaintProperty(layer, 'circle-opacity', opacityExpression);
        map.setPaintProperty(layer, 'circle-stroke-opacity', opacityExpression);

        currentFrame++;
        requestAnimationFrame(animate);
      };

      animate();
    };

    const addTransportModeIcons = async () => {
      if (!hasTransportModes) return;

      const boatImage = await map.loadImage('/assets/map/boat.png');
      const busImage = await map.loadImage('/assets/map/bus.png');
      const carImage = await map.loadImage('/assets/map/car.png');
      const planeImage = await map.loadImage('/assets/map/planeTakeOff.png');
      const trainImage = await map.loadImage('/assets/map/train.png');

      const imageMap = {
        boat: boatImage,
        bus: busImage,
        car: carImage,
        plane: planeImage,
        train: trainImage,
      };

      const transportSource = map.getSource('transport-mode');

      const getMarkersLayerId = () => {
        const layers = map.getStyle().layers;

        return (
          layers?.find(layer => layer.id === 'markers-clusters-halo')?.id ||
          null
        );
      };

      const beforeLayerId = getMarkersLayerId();

      if (transportSource) {
        const source = map.getSource(
          'transport-mode',
        ) as maptilersdk.GeoJSONSource;

        source.setData(transportModes);
      } else {
        map.addSource('transport-mode', {
          data: transportModes,
          type: 'geojson',
        });

        map.addLayer(
          {
            filter: ['!', ['has', 'point_count']],
            id: 'circles-markers',
            paint: {
              'circle-color': styles.clusterBackgroundHover,
              'circle-radius': 12,
              'circle-stroke-color': styles.clusterStrokeDefault,
              'circle-stroke-width': 1,
            },
            source: 'transport-mode',
            type: 'circle',
          },
          beforeLayerId,
        );

        for (const feature of transportModes.features) {
          const imageName = `image-${feature.properties.icon}`;
          const layerId = `transport-mode-icons-${feature.properties.icon}`;

          const hasImage = map.hasImage(imageName);
          const hasTransportModeLayer = map.getLayer(layerId);

          if (!hasImage) {
            map.addImage(imageName, imageMap[feature.properties.icon]?.data);
          }

          if (!hasTransportModeLayer) {
            map.addLayer(
              {
                filter: ['==', ['get', 'icon'], feature.properties.icon],
                id: layerId,
                layout: {
                  'icon-allow-overlap': true,
                  'icon-image': imageName,
                  'icon-size': 0.6,
                },
                source: 'transport-mode',
                type: 'symbol',
              },
              beforeLayerId,
            );
          }
        }
      }
    };

    const connectMarkersWithLine = async () => {
      if (!map || !items.markers || !items.markers.features.length) return;

      const lineCoordinates = items.markers.features.map(
        feature => feature.geometry.coordinates,
      );

      const lineData: GeoJSON.Feature = {
        geometry: {
          coordinates: lineCoordinates,
          type: 'LineString',
        },
        properties: {},
        type: 'Feature',
      };

      const markersLine = map.getSource('markers-line');

      if (markersLine) {
        const source = map.getSource(
          'markers-line',
        ) as maptilersdk.GeoJSONSource;

        source.setData(lineData);
      } else {
        map.addSource('markers-line', {
          data: lineData,
          type: 'geojson',
        });

        map.addLayer({
          id: 'markers-line-layer',
          paint: {
            'line-color': styles.markerLineDefault,
            'line-width': 4,
          },
          source: 'markers-line',
          type: 'line',
        });
      }

      await addTransportModeIcons();
    };

    const createClusterSource = (sourceId, data) => {
      if (map.getSource(sourceId)) return;

      let hoveredClusterId = null;

      map.addSource(sourceId, {
        cluster: true,
        clusterMaxZoom: 16,
        clusterRadius: 40,
        data: data,
        type: 'geojson',
      });

      map.addLayer({
        filter: ['has', 'point_count'],
        id: `${sourceId}-clusters-halo`,
        paint: {
          'circle-color': styles.clusterOuterStrokeDefault,
          'circle-opacity': parseFloat(styles.clusterOuterStrokeOpacity),
          'circle-radius': 28,
        },
        source: sourceId,
        type: 'circle',
      });

      map.addLayer({
        filter: ['has', 'point_count'],
        id: `${sourceId}-clusters`,
        paint: {
          'circle-color': styles.clusterBackgroundDefault,
          'circle-radius': 20,
          'circle-stroke-color': styles.clusterStrokeDefault,
          'circle-stroke-width': 1,
        },
        source: sourceId,
        type: 'circle',
      });

      map.addLayer({
        filter: ['has', 'point_count'],
        id: `${sourceId}-clusters-hover`,
        paint: {
          'circle-color': styles.clusterBackgroundHover,
          'circle-opacity': 0,
          'circle-radius': 20,
          'circle-stroke-color': styles.clusterStrokeHover,
          'circle-stroke-opacity': 0,
          'circle-stroke-width': 1,
        },
        source: sourceId,
        type: 'circle',
      });

      map.addLayer({
        filter: ['has', 'point_count'],
        id: `${sourceId}-clustersCount`,
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
          'text-size': 18,
        },
        source: sourceId,
        type: 'symbol',
      });

      map.on('click', `${sourceId}-clusters`, async event => {
        const features: any = map.queryRenderedFeatures(event.point, {
          layers: [`${sourceId}-clusters`],
        });
        const clusterId = features[0].properties.cluster_id;
        const zoom = await map
          .getSource(sourceId)
          .getClusterExpansionZoom(clusterId);

        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom,
        });
      });

      map.on('mouseenter', `${sourceId}-clusters`, event => {
        const features = map.queryRenderedFeatures(event.point, {
          layers: [`${sourceId}-clusters`],
        });

        if (features.length > 0) {
          const clusterId = features[0].properties.cluster_id;

          hoveredClusterId = clusterId;
          animateOpacity(`${sourceId}-clusters-hover`, clusterId, 0, 1);
          map.getCanvas().style.cursor = 'pointer';
        }
      });

      map.on('mouseleave', `${sourceId}-clusters`, () => {
        animateOpacity(`${sourceId}-clusters-hover`, hoveredClusterId, 1, 0);
        map.getCanvas().style.cursor = '';
      });
    };

    const createMarkers = () => {
      for (const feature of items.markers.features) {
        const id = feature.properties.id;
        const markerElement = document.createElement('div');

        markerElement.className = 'marker';
        markerElement.classList.add(`marker-${id}`);

        let icon: IconName = feature.properties.type;
        let customClasses: string[] = [];

        const customData =
          additionalStyles?.length > 0 &&
          additionalStyles?.find(item => item.id === id);

        if (customData) {
          icon = customData.icon || feature.properties.type;
          customClasses = customData.classes || [];
        }

        markerElement.classList.add(...customClasses);
        markerElement.innerHTML = `
              <span class="icon">${renderIcon(icon, 20)}</span>
              <span>${feature.properties.markerLabel}</span>
            `;

        markerElement.addEventListener('click', () => {
          document
            .querySelector('.marker.isActive')
            ?.classList.remove('isActive');

          markerElement.classList.add('isActive');

          if (!feature) return;

          onClickMarker?.(feature);
        });

        if (activeItem?.properties.id === id) {
          markerElement.classList.add('isActive');
        }

        markersRef.current[id] = {
          item: new maptilersdk.Marker({
            element: markerElement,
          }).setLngLat(feature.geometry.coordinates),
        };
      }
    };

    const createPois = () => {
      for (const feature of items.pois.features) {
        const id = feature.properties.id;
        const poiElement = document.createElement('div');

        const icon: IconName = feature.properties.type;

        poiElement.className = 'poi';
        poiElement.classList.add(`poi-${id}`);

        poiElement.innerHTML = `
      <span class="icon">${renderIcon(icon, 16)}</span>
      <span>${feature.properties.poiLabel}</span>
    `;

        poisRef.current[id] = {
          item: new maptilersdk.Marker({
            element: poiElement,
          }).setLngLat(feature.geometry.coordinates),
        };
      }
    };

    const createLandmarks = () => {
      for (const feature of items.landmarks.features) {
        const id = feature.properties.id;
        const landmarkElement = document.createElement('div');
        const labelWrapper = document.createElement('div');
        const label = document.createElement('span');

        const icon: IconName = feature.properties.type;

        labelWrapper.className = 'label-wrapper';
        label.className = 'label';
        label.textContent = feature.properties.landmarkLabel;
        labelWrapper.appendChild(label);

        landmarkElement.className = 'landmark';
        landmarkElement.classList.add(`landmark-${id}`);
        landmarkElement.innerHTML = `
      <div class="icon-wrapper">
        <span class="icon">${renderIcon(icon, 16)}</span>
      </div>
    `;
        landmarkElement.appendChild(labelWrapper);

        landmarkElement.addEventListener('click', () => {
          if (!feature) return;

          onClickLandmark?.(feature);
        });

        landmarksRef.current[id] = {
          item: new maptilersdk.Marker({
            element: landmarkElement,
          }).setLngLat(feature.geometry.coordinates),
        };

        landmarkElement.addEventListener('mouseenter', () => {
          labelWrapper.style.width = `${labelWrapper.scrollWidth}px`;
          labelWrapper.style.opacity = '1';
          landmarkElement.style.zIndex = '1';
        });

        landmarkElement.addEventListener('mouseleave', () => {
          labelWrapper.style.width = '0';
          labelWrapper.style.opacity = '0';
          landmarkElement.style.zIndex = '0';
        });
      }
    };

    useEffect(() => {
      if (!mapRef.current) return;

      const handleChange = () => {
        onFullscreenChangeRef.current(getIsFullScreen());
      };

      FULLSCREEN_CHANGE_EVENTS.forEach(eventName =>
        document.addEventListener(eventName, handleChange),
      );

      return () => {
        FULLSCREEN_CHANGE_EVENTS.forEach(eventName =>
          document.removeEventListener(eventName, handleChange),
        );
      };
    }, []);

    useEffect(() => {
      if (!mapRef.current) return;

      if (map) {
        map.remove();
      }

      map = mapConfig.createMap(mapRef, controlPosition);

      if (!map) return;

      if (activeItem && items.pois?.features.length === 0) {
        map.flyTo({
          center: activeItem.geometry.coordinates,
          duration: 0,
          zoom: flyToZoom,
        });
      } else if (bounds) {
        map.fitBounds(bounds, {
          duration: 0,
          maxZoom: FIT_BOUNDS_MAX_ZOOM,
          padding: FIT_BOUNDS_PADDING,
        });
      }
    }, []);

    useEffect(() => {
      if (!map) return;

      if (!activeItem && bounds) {
        map.fitBounds(bounds, {
          duration: MAP_ANIMATION_DURATION,
          maxZoom: FIT_BOUNDS_MAX_ZOOM,
          padding: FIT_BOUNDS_PADDING,
        });
      }

      removeActiveStyles();

      if (!activeItem) return;

      const activeMarker = markersRef.current[activeItem.properties.id]?.item;

      if (activeMarker) {
        activeMarker.getElement().classList.add('isActive');
      }

      if (!isMapLoaded) return;

      map.flyTo({
        center: activeItem.geometry.coordinates,
        duration: MAP_ANIMATION_DURATION,
        zoom: flyToZoom,
      });
    }, [activeItem]);

    useEffect(() => {
      if (!map || !bounds) return;

      map.fitBounds(bounds, {
        duration: MAP_ANIMATION_DURATION,
        maxZoom: FIT_BOUNDS_MAX_ZOOM,
        padding: FIT_BOUNDS_PADDING,
      });
    }, [bounds]);

    /**
     * Markers
     */
    useEffect(() => {
      if (!map) return;

      const markersSource = map.getSource('markers');

      if (!markersSource) return;

      markersSource.setData(items.markers);
    }, [items.markers]);

    useEffect(() => {
      if (!map || !items.markers || !items.markers.features.length) return;

      markersRef.current = {};
      removeActiveStyles();

      createMarkers();

      const markersSource = map.getSource('markers');

      if (markersSource) return;

      map.on('load', async () => {
        if (map.getSource('markers')) return;

        if (connectMarkers) {
          await connectMarkersWithLine();
        }

        createClusterSource('markers', items.markers);

        map.on('zoomend', async () => {
          if (!hasTransportModes) return;

          const features = await getVisibleUniqueFeatures('markers');

          const fullyZoomedOut =
            features.clusters.length === 1 && features.markers.length === 0;

          if (fullyZoomedOut) {
            removeSource('transport-mode');
          } else {
            await addTransportModeIcons();
          }
        });
      });
    }, [items.markers]);

    /**
     * Pois
     */
    useEffect(() => {
      if (!map) return;

      const poisSource = map.getSource('pois');

      if (!poisSource) return;

      poisSource.setData(items.pois);
    }, [items.pois]);

    useEffect(() => {
      if (!map || !items.pois) return;

      poisRef.current = {};

      createPois();

      const poisSource = map.getSource('pois');

      if (poisSource) return;

      map.on('load', async () => {
        if (map.getSource('pois')) return;

        createClusterSource('pois', items.pois);
      });
    }, [items.pois]);

    /**
     * Landmarks
     */
    useEffect(() => {
      if (!map) return;

      const landmarksSource = map.getSource('landmarks');

      if (!landmarksSource) return;

      landmarksSource.setData(items.landmarks);
    }, [items.landmarks]);

    useEffect(() => {
      if (!map || !items.landmarks) return;

      landmarksRef.current = {};

      createLandmarks();

      const landmarksSource = map.getSource('landmarks');

      if (landmarksSource) return;

      map.on('load', async () => {
        if (map.getSource('landmarks')) return;

        createClusterSource('landmarks', items.landmarks);
      });
    }, [items.landmarks]);

    /**
     * Visible items on the map
     */
    useEffect(() => {
      if (!map || !mapRef.current || !items.markers) return;

      map.on('load', () => {
        let landmarksOnScreen: MarkersCollection = {};
        let markersOnScreen: MarkersCollection = {};
        let poisOnScreen: MarkersCollection = {};
        const currentLandmarks: MarkersCollection = {};
        const currentMarkers: MarkersCollection = {};
        const currentPois: MarkersCollection = {};

        const updateItems = (
          sourceName: string,
          items: MarkersCollection,
          itemsOnScreen: MarkersCollection,
          itemsRef: React.MutableRefObject<Record<string, MarkerRef>>,
        ): MarkersCollection => {
          const features = map.querySourceFeatures(sourceName) || [];

          if (!features) return {};

          const newItems: MarkersCollection = {};

          for (const feature of features) {
            if (feature.properties.cluster) continue;

            const id = feature.properties.id;

            let item = items[id];

            if (!item) {
              item = itemsRef.current[id]?.item;
              items[id] = item;
            }

            newItems[id] = item;

            if (!itemsOnScreen[id]) item.addTo(map);
          }

          for (const id in itemsOnScreen) {
            if (!newItems[id]) itemsOnScreen[id].remove();
          }

          return newItems;
        };

        map.on('render', () => {
          const landmarksSource = map.getSource('landmarks');
          const markersSource = map.getSource('markers');
          const poisSource = map.getSource('pois');

          if (markersSource) {
            markersOnScreen = updateItems(
              'markers',
              currentMarkers,
              markersOnScreen,
              markersRef,
            );
          }

          if (poisSource) {
            poisOnScreen = updateItems(
              'pois',
              currentPois,
              poisOnScreen,
              poisRef,
            );
          }

          if (landmarksSource) {
            landmarksOnScreen = updateItems(
              'landmarks',
              currentLandmarks,
              landmarksOnScreen,
              landmarksRef,
            );
          }
        });

        setIsMapLoaded(true);
      });
    }, []);

    return (
      <div className={classNames(styles.component, className)}>
        <div ref={mapRef} className={styles.map}>
          {getIsFullScreen() && children && (
            <React.Fragment>{children}</React.Fragment>
          )}
        </div>
      </div>
    );
  },
);

Map.displayName = 'Map';

export default Map;
